import { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";
import "bootstrap-icons/font/bootstrap-icons.css";
import LocalStorage from "../../helpers/localstorage";
import { toast } from "react-toastify";
import { setupAPIclient } from '../../services/setupAPIclient';
import Layout from '../../components/layoutConfig/layout';

function PlanSubmasterPage() {
  const localStorage = new LocalStorage();
  const token = String(localStorage.getAuthToken());
  const ApiClient = setupAPIclient(token);

  const [planoSelecionado, setPlanoSelecionado] = useState<string | null>(null);
  const [metodoPagamento, setMetodoPagamento] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{ pixCode: string; qrCodeBase64Url: string; expiration: string } | null>(null);
  const [tempoRestante, setTempoRestante] = useState<number>(0);
  const [loadingPix, setLoadingPix] = useState<boolean>(false);
  const [checkingPayment, setCheckingPayment] = useState<boolean>(false);

  const planos = [
    { nome: "Essencial", preco: "R$ 49/mês", key: "essencial", atualPlan: false },
    { nome: "Profissional", preco: "R$ 79/mês", key: "profissional", atualPlan: false },
    { nome: "Premium", preco: "R$ 119/mês", key: "premium", atualPlan: false },
  ];

  const funcionalidades = [
    { nome: "Comunicação via WhatsApp", gratuito: "Incluso", essencial: "Incluso", profissional: "Incluso", premium: "Incluso", icon: "bi-whatsapp" },
    { nome: "Dashboards financeiros", gratuito: "Incluso", essencial: "Básico", profissional: "Avançado", premium: "Completo", icon: "bi-graph-up-arrow" },
    { nome: "Criação de negócio", gratuito: "Incluso", essencial: "Incluso", profissional: "Incluso", premium: "Incluso", icon: "bi-building" },
    { nome: "Relatórios mensais", gratuito: "Incluso", essencial: "Simplificados", profissional: "Avançados", premium: "Avançados completos", icon: "bi-bar-chart-line" },
    { nome: "Suporte prioritário", gratuito: "Não incluso", essencial: "Não disponível", profissional: "Limitado", premium: "Completo", icon: "bi-headset" },
  ];

  // Atualiza contador do Pix
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (tempoRestante > 0) {
      timer = setInterval(() => {
        setTempoRestante((prev) => prev - 1);
      }, 8000);
    }
    return () => clearInterval(timer);
  }, [tempoRestante]);

  // Verificação automática do pagamento a cada 5s
  useEffect(() => {
    if (!pixData) return;
    if (checkingPayment) return;

    const interval = setInterval(() => {
      handleVerifyPayment();
    }, 5000);

    return () => clearInterval(interval);
  }, [pixData]);

  const handlePagar = (plano: string, metodo: string) => {
    setPixData(null);
    setTempoRestante(0);
    if (metodo === "Pix") setLoadingPix(true);
    handlesubmitPix(plano.toLowerCase(), metodo);
  };

  const handlesubmitPix = async (plano: string, payment_type: string) => {
    if (payment_type === "Pix") {
      try {
        const response = await ApiClient.post(`/management/checkout/pix`, {
          type_of_plan: plano,
        });

        setPixData({
          pixCode: response.data.pixCode,
          qrCodeBase64Url: response.data.qrCodeBase64Url,
          expiration: response.data.expiration,
        });

        setTempoRestante(20 * 60); // 20 minutos
      } catch (error: any) {
        console.error("Erro ao gerar Pix:", error);
        toast.error("Erro ao gerar Pix. Tente novamente.");
      } finally {
        setLoadingPix(false);
      }
    } else {
      toast.info("Pagamento via cartão ainda não implementado.");
      setLoadingPix(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!pixData) return;

    setCheckingPayment(true);
    try {
      const response = await ApiClient.post(`/management/checkout/verifyPaymentPix`);
      const status = response.data.payment_status;

      if (status === "PAID") {
        toast.success("✅ Pagamento confirmado com sucesso!");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (status === "WARNING") {
        toast.info("⏳ Pagamento ainda pendente, aguarde...");
      } else {
        toast.error("⚠️ Status desconhecido. Tente novamente.");
      }
    } catch (error) {
      console.error("Erro ao verificar pagamento:", error);
      toast.error("Erro ao verificar pagamento Pix.");
    } finally {
      setCheckingPayment(false);
    }
  };

  const copiarPix = () => {
    if (pixData) {
      navigator.clipboard.writeText(pixData.pixCode);
      toast.success("📋 Código Pix copiado para a área de transferência!");
    }
  };

  const formatarTempo = (segundos: number) => {
    const min = Math.floor(segundos / 60).toString().padStart(2, "0");
    const sec = (segundos % 60).toString().padStart(2, "0");
    return `${min}:${sec}`;
  };

  return (
    <Layout>
      <div className="py-2">
        <h2 className="text-center fw-bold mb-3" style={{color:"#5dade2"}}>Potencialize seu crescimento 🚀</h2>
        <p className="text-center text-muted mb-5">
          Escolha o plano ideal para expandir seu negócio com os melhores recursos.
        </p>

        {/* Cards */}
        <div className="row g-4">
          {planos.map((plano) => (
            <div key={plano.key} className="col-12 col-md-6 col-lg-4">
              <div
                className={`card h-100 shadow-sm border-0 plano-card ${
                  planoSelecionado === plano.key ? "border border-primary shadow-lg" : ""
                }`}
                style={{ cursor: "pointer", transition: "all 0.3s ease" }}
              >
                <div className="card-body d-flex flex-column">
                  <h5 className="card-title fw-bold text-center">{plano.nome}</h5>
                  <p className="text-center text-primary fs-5 fw-bold">{plano.preco}</p>
                  <ul className="list-unstyled mt-3 flex-grow-1">
                    {funcionalidades
                      .filter((f) => f[plano.key] && f[plano.key] !== "Não disponível")
                      .map((f, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center">
                          <i style={{color:"#5dade2"}} className={`bi ${f.icon} me-2 fs-5`}></i>
                          <span>
                            <strong>{f.nome}:</strong> {f[plano.key]}
                          </span>
                        </li>
                      ))}
                  </ul>

                  {plano.atualPlan ? (
                    <div>
                      <span className="badge bg-primary text-white px-3 py-2">Plano atual</span>
                    </div>
                  ) : (
                    <button
                      className="btn mt-auto w-100"
                      data-bs-toggle="modal"
                      style={{background:"#5dade2", color:"white"}}
                      data-bs-target="#pagamentoModal"
                      onClick={() => {
                        setPlanoSelecionado(plano.key);
                        setMetodoPagamento(null);
                        setPixData(null);
                        setTempoRestante(0);
                        setLoadingPix(false);
                      }}
                    >
                      Selecionar plano
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal */}
      <div
        className="modal fade"
        id="pagamentoModal"
        tabIndex={-1}
        aria-labelledby="pagamentoModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-4">
            <div className="modal-header text-white rounded-top-4" style={{background:"#006572"}}>
              <h5 className="modal-title mb-3 mt-1" id="pagamentoModalLabel" style={{color:"white"}}>Finalizar assinatura</h5>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <div className="modal-body text-center">
              <p className="mb-4 fs-6">
                Você selecionou o plano:{" "}
                <strong className="text-primary">
                  {planoSelecionado && planos.find((p) => p.key === planoSelecionado)?.nome}
                </strong>
              </p>

              <p className="fw-semibold mb-3">Escolha o método de pagamento:</p>

              <div className="d-flex justify-content-around gap-3 mb-4">
                <button
                  className={`btn btn-lg ${
                    metodoPagamento === "Cartão de Crédito" ? "btn-primary" : "btn-outline-primary"
                  } d-flex flex-column align-items-center px-4 py-3 w-100`}
                  onClick={() => setMetodoPagamento("Cartão de Crédito")}
                >
                  <i className="bi bi-credit-card fs-2 mb-2"></i>
                  Cartão de Crédito
                </button>

                <button
                  className={`btn btn-lg ${
                    metodoPagamento === "Pix" ? "btn-success" : "btn-outline-success"
                  } d-flex flex-column align-items-center px-4 py-3 w-100`}
                  onClick={() => setMetodoPagamento("Pix")}
                >
                  <i className="bi bi-qr-code fs-2 mb-2"></i>
                  Pix
                </button>
              </div>

              {metodoPagamento && (
                <button
                  className="btn btn-lg btn-success w-100 fw-bold shadow-sm"
                  onClick={() =>
                    handlePagar(planos.find((p) => p.key === planoSelecionado)?.nome || "", metodoPagamento)
                  }
                >
                  {loadingPix ? "⏳ Gerando Pix..." : "✅ Confirmar Pagamento"}
                </button>
              )}

              {/* Exibe Pix */}
              {pixData && !loadingPix && (
                <div className="mt-4 text-center">
                  <p className="fw-semibold">O Pix expira em: {formatarTempo(tempoRestante)}</p>
                  <p className="fw-semibold">Após o pagamento, o comprovante será enviado por e-mail.</p>
                  <img src={pixData.qrCodeBase64Url} alt="QR Code Pix" className="mb-3" style={{ maxWidth: "200px" }} />
                  <textarea className="form-control text-center mb-2" readOnly value={pixData.pixCode} style={{ fontSize: "0.85rem", height: "80px" }} />
                  <button className="btn btn-outline-primary w-100 mb-2" onClick={copiarPix}>
                    📋 Copiar código Pix
                  </button>
                </div>
              )}
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancelar</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .plano-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.25) !important;
        }
        .btn-lg {
          border-radius: 12px;
        }
      `}</style>
    </Layout>
  );
}

export default PlanSubmasterPage;
